<template>
    <div>
        <!-- external order state -->
        <div class="d-flex">
            <v-chip-group column>
                <v-chip
                    v-for="(externalOrderState, i) in order.external_order_states"
                    :key="i"
                    :color="stateColor(externalOrderState)"
                    class="mb-3"
                >
                    {{ $t('orders.external_order_states.' + externalOrderState) }}
                </v-chip>
                <v-chip :color="reservationStatusColor(order.reservation_status)" class="mb-3">
                    {{ reservationStatusLabel(order.reservation_status) }}
                </v-chip>
            </v-chip-group>
            <v-spacer/>
            <v-btn
                v-if="!hideEditButton"
                color="primary"
                @click="onEdit"
                :disabled="!canEditOrder"
                class="mr-1"
            >
                <v-icon left>mdi-pencil</v-icon>
                {{ $t('orders.edit') }}
            </v-btn>
            <v-btn
                v-if="!hideApproveButton"
                color="warning"
                @click="onApprove"
                :loading="isBusyApprove"
                :disabled="!canApproveOrder"
            >
                <v-icon left>mdi-check-decagram-outline</v-icon>
                {{ $t('orders.approve') }}
            </v-btn>
        </div>
        <v-alert
            v-if="order.provider_error_message"
            type="error"
            dense
            outlined
            class="mb-3"
        >
            {{ order.provider_error_message }}
        </v-alert>
        <v-alert
            v-else-if="order.reservation_status === 'failed' && order.reservation_error_message"
            type="error"
            dense
            outlined
            class="mb-3"
        >
            {{ order.reservation_error_message }}
        </v-alert>
        <!-- products -->
        <v-card outlined class="mb-3">
            <v-card-title>{{ $t('orders.products') }}</v-card-title>
            <v-card-text>
                <v-list class="py-0">
                    <template v-for="(product, i) in products">
                        <v-list-item :key="`item-` + i">
                            <v-list-item-avatar>
                                <v-img :src="product.image_url"></v-img>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ product.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ $t('orders.brand') }}: <b>{{ product.brand ? product.brand : '-' }}</b>
                                </v-list-item-subtitle>
                                <v-list-item-subtitle>
                                    {{ $t('orders.model') }}: <b>{{ product.model ? product.model : '-' }}</b>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-list-item-action-text>
                                    {{ product.quantity }} {{ $t('products.pieces') }}
                                </v-list-item-action-text>
                            </v-list-item-action>
                        </v-list-item>
                        <v-divider v-if="i < products.length - 1" :key="`divider-` + i"/>
                    </template>
                </v-list>
            </v-card-text>
        </v-card>
        <v-row align="stretch">
            <v-col cols="12" lg="6">
                <!-- recipient -->
                <v-card outlined class="mb-3" height="100%">
                    <v-card-title>{{ $t('orders.recipient') }}</v-card-title>
                    <v-card-text>
                        {{ order.address.name }}<br>
                        {{ $t('addresses.ico') }}: {{ order.address.ico ? order.address.ico : '-' }}<br>
                        {{ $t('addresses.dic') }}: {{ order.address.dic ? order.address.dic : '-' }}<br>
                        {{ $t('addresses.email') }}: {{ order.address.email ? order.address.email : '-' }}<br>
                        {{ $t('addresses.phone') }}: {{ order.address.phone ? order.address.phone : '-' }}
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" lg="6">
                <!-- message -->
                <v-card outlined class="mb-3" height="100%">
                    <v-card-title>{{ $t('orders.message') }}</v-card-title>
                    <v-card-text>{{ order.message ? order.message : '' }}</v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import SnackbarMixin from "@/mixins/SnackbarMixin";
import {API} from "@/api/api";

export default {
    name: "OrderDetail",
    mixins: [SnackbarMixin],
    props: {
        order: {
            type: Object,
            required: true
        },
        hideApproveButton: {
            type: Boolean,
            required: false,
            default: () => false
        },
        hideEditButton: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    data: () => ({
        // loading
        isBusyApprove: false
    }),
    computed: {
        products() {
            return this.order.products;
        },
        addressString() {
            const address = this.order.address;
            return [
                ("" + (address.street ? address.street : "") + " " + (address.house_number ? address.house_number : "")).trim(),
                ("" + (address.suburb ? address.suburb : "")).trim(),
                ("" + (address.postal_code ? address.postal_code : "") + " " + (address.city ? address.city : "")).trim(),
                ("" + (address.country ? address.country : "")).trim()
            ].filter(x => x).join(', ');
        },
        isApprover() {
            let roles = this.$store.getters['identity/getRoles'];
            return roles && roles.includes('ROLE_APPROVER');
        },
        canApproveOrder() {
            return this.isApprover
                && this.order.state == 0
                && this.order.external_id === null
                && this.canApproveReservation
        },
        canEditOrder() {
            return this.isApprover && this.order.state == 0 && !this.isReservationActionLocked
        },
        canApproveReservation() {
            return ['active', 'cancelled'].includes(this.order.reservation_status);
        },
        isReservationActionLocked() {
            return ['pending', 'cancel_pending'].includes(this.order.reservation_status);
        }
    },
    methods: {
        onApprove() {
            this.isBusyApprove = true;
            setTimeout(() => {
                API.post("/api/orders/" + this.order.id + "/approve")
                    .then(() => {
                        this.snackbarSuccess(this.$t('snackbar.order_approved_successfully'));
                        this.$router.push({name: "approval"});
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyApprove = false;
                    });
            }, 3000);
        },
        onEdit() {
            this.$router.push({name: "edit-order", params: {"orderId": this.order.id}})
        },
        stateColor(externalOrderState) {
            switch (externalOrderState) {
                case "EO_WAITING_FOR_APPROVAL":
                    return "warning";
                case "EO_STATE_CREATED":
                    return "info";
                case "EO_STATE_UNABLE_TO_PROCESS":
                    return "error";
                case "EO_STATE_IN_PROGRESS":
                    return "warning";
                case "EO_STATE_WAITING":
                    return "warning";
                case "EO_STATE_STOCK_PICKING_WAITING":
                    return "warning";
                case "EO_STATE_STOCK_PICKING_IN_PROGRESS":
                    return "warning";
                case "EO_STATE_STOCK_PICKING_COMPLETED":
                    return "success";
                case "EO_STATE_STOCK_PICKING_CANCELED":
                    return "secondary";
                case "EO_STATE_PREPARE_PACKAGE_WAITING":
                    return "warning";
                case "EO_STATE_PREPARE_PACKAGE_IN_PROGRESS":
                    return "warning";
                case "EO_STATE_PREPARE_PACKAGE_COMPLETED":
                    return "success";
                case "EO_STATE_PREPARE_PACKAGE_CANCELED":
                    return "secondary";
                case "EO_STATE_SHIPMENTS_WAITING":
                    return "warning";
                case "EO_STATE_SHIPMENT_CANCELLED":
                    return "secondary";
                case "EO_STATE_SHIPPED":
                    return "success";
                case "EO_STATE_SENT":
                    return "success";
                case "EO_STATE_BEING_DELIVERED":
                    return "success";
                case "EO_STATE_DELIVERED_TO_PICKUP_LOCATION":
                    return "success";
                case "EO_STATE_LOST":
                    return "error";
                case "EO_STATE_DELIVERED":
                    return "success";
                case "EO_STATE_RETURNING":
                    return "warning";
                case "EO_STATE_RETURNED":
                    return "secondary";
                case "EO_STATE_CANCELLED":
                    return "secondary";
                case "EO_STATE_CLOSED":
                    return "success";
                default:
                    return "secondary";
            }
        },
        reservationStatusLabel(status) {
            return this.$t('orders.reservation_status.' + (status || 'unknown'));
        },
        reservationStatusColor(status) {
            switch (status) {
                case "active":
                    return "success";
                case "pending":
                case "cancel_pending":
                    return "warning";
                case "failed":
                    return "error";
                case "cancelled":
                    return "info";
                case "not_required":
                    return "secondary";
                default:
                    return "secondary";
            }
        }
    }
}
</script>
